<template>
  <el-dialog
    v-model="dialogVisible"
    width="45%"
    :close-on-click-modal="false"
    class="ai-result-dialog"
    :destroy-on-close="true"
    :draggable="true"
    :resizable="false"
  >
    <template #header>
      <div class="custom-header">
        <span class="el-dialog__title" :class="getTitleClass(title)">{{ title }}</span>
        <div class="font-size-control">
          <el-button size="small" circle @click="decreaseFontSize">
            <el-icon><Minus /></el-icon>
          </el-button>
          <span class="font-size-value">{{ fontSize }}%</span>
          <el-button size="small" circle @click="increaseFontSize">
            <el-icon><Plus /></el-icon>
          </el-button>
          <el-button size="small" @click="resetFontSize">重置</el-button>
          <span class="control-divider"></span>
          <span class="line-height-label">行距:</span>
          <el-button size="small" circle @click="decreaseLineHeight">
            <el-icon><Minus /></el-icon>
          </el-button>
          <span class="line-height-value">{{ lineHeight }}</span>
          <el-button size="small" circle @click="increaseLineHeight">
            <el-icon><Plus /></el-icon>
          </el-button>
        </div>
      </div>
    </template>
    <div v-if="isAdmin" class="result-content">
      <el-input
        v-model="editableContent"
        type="textarea"
        :rows="15"
        resize="none"
        placeholder="结果内容..."
      />
    </div>
    <div v-else class="result-content">
      <div class="content-header" v-if="title === '术语检查结果' || title === '术语' || title.includes('术语')">
        <el-switch
          v-model="testMode"
          active-text="检测模式"
          inactive-text="查看模式"
          @change="handleModeChange"
        />
        <span class="mode-description">{{ testMode ? '测试自己对术语的掌握程度' : '查看所有术语及译文' }}</span>
      </div>
      
      <!-- 查看模式：显示所有术语及译文 -->
      <div v-if="!testMode" v-html="formattedContent" :class="{'formatted-content': true, 'machine-translation-result': title === '译文', 'score-result': title === '翻译评分结果' || title === '历史评分结果' || title.includes('评分结果')}" :style="{ fontSize: fontSize + '%', lineHeight: lineHeight }"></div>
      
      <!-- 检测模式：只显示测试面板 -->
      <div v-else class="test-mode-container">
        <div v-if="selectedTerm" class="test-panel">
          <div class="selected-term">当前术语: <strong>{{ selectedTerm.term }}</strong></div>
          <el-input 
            v-model="userInput" 
            placeholder="请输入该术语的英文翻译"
            @keyup.enter="checkAnswer"
          ></el-input>
          <div class="action-buttons">
            <el-button size="small" @click="checkAnswer">检查</el-button>
            <el-button size="small" @click="showAnswer">查看答案</el-button>
            <el-button size="small" @click="nextTerm">下一个</el-button>
          </div>
          <div v-if="feedbackMessage" :class="['feedback', feedbackType]">
            {{ feedbackMessage }}
          </div>
        </div>
      </div>
    </div>
    
    <template #footer>
      <div class="dialog-footer" v-if="isAdmin">
        <el-button type="primary" @click="handleSave">保存</el-button>
      </div>
      <div class="dialog-footer" v-else-if="testMode">
        <el-button @click="exitTestMode">退出检测</el-button>
      </div>
      <div class="dialog-footer" v-else-if="(title === '翻译评分结果' || title === '历史评分结果' || title.includes('评分结果')) && !isAdmin && isLoggedIn">
        <el-text type="info">已自动保存</el-text>
        <el-button type="primary" @click="handleSaveToSupabase">重新保存</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { computed, ref, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import { ApiService } from '../service/api'
import { Plus, Minus } from '@element-plus/icons-vue'
import { sanitizeAIContent } from '@/utils/sanitize'

export default {
  name: 'AIResultDialog',
  components: {
    Plus,
    Minus
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: 'AI结果'
    },
    content: {
      type: String,
      default: ''
    }
  },
  emits: ['update:visible'],
  setup(props, { emit }) {
    const store = useStore()
    const editableContent = ref('')
    const testMode = ref(false)
    const userInput = ref('')
    const selectedTerm = ref(null)
    const feedbackMessage = ref('')
    const feedbackType = ref('') // success, error
    const allTerms = ref([])
    const fontSize = ref(100)
    const lineHeight = ref(2.0)
    
    // 判断用户是否登录
    const isLoggedIn = computed(() => !!store.state.user)
    
    // 监听content变化，更新可编辑内容
    watch(() => props.content, (newContent) => {
      editableContent.value = newContent
      if (props.title === '术语检查结果' || props.title === '术语' || props.title.includes('术语')) {
        parseTerms(newContent)
      }
    }, { immediate: true })
    
    // 解析术语内容，提取术语和翻译
    const parseTerms = (content) => {
      if (!content) {
        allTerms.value = []
        return
      }
      
      const terms = []
      const lines = content.split('\n')
      let currentCategory = ''
      
      for (const line of lines) {
        if (line.startsWith('【') && line.endsWith('】')) {
          currentCategory = line.substring(1, line.length - 1)
        } else if (line.startsWith('•') && line.includes('(') && line.includes(')')) {
          const termText = line.substring(2, line.indexOf('(')).trim()
          const translationText = line.substring(line.indexOf('(') + 1, line.lastIndexOf(')')).trim()
          
          terms.push({
            category: currentCategory,
            term: termText,
            translation: translationText,
            line: line
          })
        }
      }
      
      allTerms.value = terms
    }
    
    const dialogVisible = computed({
      get: () => props.visible,
      set: (value) => emit('update:visible', value)
    })

    const isAdmin = computed(() => store.state.isAdmin)
    
    // 格式化后的内容（正常查看模式）
    const formattedContent = computed(() => {
      if (!props.content) return ''

      // 首先对输入内容进行消毒，防止 XSS
      const sanitizedInput = sanitizeAIContent(props.content)

      // 先处理掉内容中可能存在的连续空行
      let processedContent = sanitizedInput
        .replace(/\n\s*\n/g, '\n') // 替换多个空行为单个换行
        .trim() // 确保内容前后没有多余空白
      
      // 机器翻译结果特殊处理：使段落之间有空行
      if (props.title === '译文') {
        // 将单个换行符替换为段落标签，实现段落间空行和两端对齐
        return `<div class="machine-translation-content">${
          processedContent.split('\n')
            .filter(para => para.trim()) // 过滤掉空段落
            .map(para => 
              `<p class="translation-paragraph">${para}</p>`
            )
            .join('')
        }</div>`
      }
      
      // 预处理：在"解析:"行结束后添加一个额外的换行，确保与下一个高危点之间有空行
      processedContent = processedContent.replace(/(解析:.*)\n(•\s*高危点:)/g, '$1\n\n$2')
      
      // 评分结果特殊处理 - 同时处理"翻译评分结果"和"历史评分结果"以及任何包含"评分结果"的标题
      if (props.title === '翻译评分结果' || props.title === '历史评分结果' || props.title.includes('评分结果')) {
        // 先进行一些基础的格式清理
        processedContent = processedContent
          .replace(/•\s*总分：/g, '总分：')  // 移除总分前的点符号
          .replace(/【总分】\s*•/g, '【总分】')  // 移除总分标题后的点符号
          .replace(/【总分】\s*<br>\s*•/g, '【总分】') // 移除总分标题下方的点符号
          // 处理类似 "【1. 欧化表达问题 (-d+分)】" 的特殊格式，移除数字前缀
          .replace(/【\d+\.\s*([^】]+?)】/g, (match, p1) => {
            return `【${p1.trim()}】`;
          })
          .trim();
        
        // 使用更简洁的格式，类似解析对话框的样式
        processedContent = processedContent
          // 处理总分行 - 将标题和内容放在同一行，使用相同的字体大小
          .replace(/【总分】\s*\n总分：/g, '<div class="score-header"><span class="score-title">【总分】</span> <span class="score-value">总分：</span></div>')
          .replace(/总分：(-?\d+)\/50/g, '<span class="score-value">$1/50</span>')
          
          // 处理扣分详情标题
          .replace(/【扣分详情】/g, '<div class="section-title">【扣分详情】</div>')
          
          // 处理分类标题与分数 - 确保颜色正确
          .replace(/【([^】\n]+?)】\s*\((-\d+分)\)/g, 
                  '<div class="category-title"><span style="color:#67C23A;">【$1】</span><span class="deduction">$2</span></div>')
          
          // 处理所有【】标题，确保应用绿色样式（但不处理已经处理过的总分和扣分详情）
          .replace(/【([^】\n总分扣分详情]+?)】/g, 
                  '<span style="color:#67C23A;">【$1】</span>')
                  
          // 处理特殊的欧化表达问题标题格式
          .replace(/【欧化表达问题】\s*\((-d\+分)\)/g,
                  '<div class="category-title"><span style="color:#67C23A;">【欧化表达问题】</span><span class="deduction">$1</span></div>')
          
          // 处理分类标题（不带编号）- 确保应用正确的class，但避免重复处理
          .replace(/【([^】\n]+?)】\s*\n(?!<div)/g, 
                  '<div class="category-title"><span style="color:#67C23A;">【$1】</span></div>\n')
          
          // 处理具体错误项 - 匹配格式 "• [具体错误] (-X分)" 或 "• 具体错误类型 (-X分)"
          .replace(/•\s*(?:\[([^[\]]+)\]|([^([<]+?))\s*\(-([\d.]+)分\)/g, 
                  '<div class="error-item">• $1$2<span class="error-deduction">(-$3分)</span></div>')
          
          // 处理原文、译文、建议和理由，让它们更简洁
          .replace(/原文：(.*)/g, '<div class="detail-item detail-original"><strong>原文：</strong>"$1"</div>')
          .replace(/译文：(.*)/g, '<div class="detail-item detail-translation"><strong>译文：</strong>"$1"</div>')
          .replace(/建议：(.*)/g, '<div class="detail-item detail-suggestion"><strong>建议：</strong>"$1"</div>')
          .replace(/理由：(.*)/g, '<div class="detail-item detail-reason"><strong>理由：</strong>$1</div>')
          
          // 处理没有问题的提示文本 - 确保这些文本使用正常字体大小
          .replace(/本次翻译中未发现明显的语法结构问题。/g, '<div class="no-issues-text">本次翻译中未发现明显的语法结构问题。</div>')
          
          // 确保详情项之间没有额外空行
          .replace(/<\/div><br><div class="detail-item/g, '</div><div class="detail-item')
          
          // 更强力地处理具体错误项与详情之间的空行
          .replace(/(<div class="error-item">.*?<\/div>)<br>(<div class="detail-item)/g, '$1$2')
          .replace(/(<div class="error-item">.*?<\/div>)<br>\s*<br>(<div class="detail-item)/g, '$1$2')
          
          // 更强力地移除原文、译文、建议、理由之间的多余空行
          .replace(/(<div class="detail-item.*?<\/div>)<br>(<div class="detail-item)/g, '$1$2')
          .replace(/(<div class="detail-item.*?<\/div>)<br>\s*<br>(<div class="detail-item)/g, '$1$2')
          
          // 确保移除所有detail-item之间的空行
          .replace(/<\/div><br><div class="detail-item/g, '</div><div class="detail-item')
          
          // 处理总评和修改建议部分
          .replace(/【总评】/g, '<div class="section-title">【总评】</div>')
          .replace(/【修改建议】/g, '<div class="section-title">【修改建议】</div>')
          .replace(/总评：/g, '<div class="section-title">【总评】</div>')
          .replace(/修改建议：/g, '<div class="section-title">【修改建议】</div>')
          
          // 替换换行符为HTML换行
          .replace(/\n/g, '<br>')
          
          // 确保错误项之间有适当的间距
          .replace(/<\/div><br><br><div class="error-item"/g, '</div><div class="error-separator"></div><div class="error-item"')
          .replace(/<\/div><br><div class="error-item"/g, '</div><div class="error-item"')
          
          // 确保类别之间有空行
          .replace(/<\/div><br><div class="category-title"/g, '</div><div class="category-separator"></div><div class="category-title"')
          
          // 清理原文、译文、建议和理由之间的任何剩余空行
          .replace(/(<\/div>)(\s*<br>\s*)+(<div class="detail)/g, '$1$3');
          
        // 将每个错误详情组（原文、译文、建议、理由）包装在一个容器中
        processedContent = processedContent
          .replace(/(<div class="detail-item detail-original">.*?<\/div>)(<div class="detail-item detail-translation">.*?<\/div>)(<div class="detail-item detail-suggestion">.*?<\/div>)(<div class="detail-item detail-reason">.*?<\/div>)/g, 
                  '<div class="detail-group">$1$2$3$4</div>')
          
        return processedContent;
      }
      
      return processedContent
        .replace(/\n/g, '<br>')
        .replace(/•/g, '&#8226;')
        // 确保类别标题后面没有额外的<br>，直接跟着内容
        .replace(/【([^】]*)】<br>/g, '<strong class="category">【$1】</strong>')
        .replace(/总分：(\d+)\/50/g, '<strong class="score">总分：$1/50</strong>')
        .replace(/\(-([\d.]+)分\)/g, '<span class="deduction">(-$1分)</span>')
        // 为高危点标题添加红色样式
        .replace(/(&#8226;\s*高危点:)/g, '<span class="high-risk-point">$1</span>')
        // 特殊处理高危点标题行，但排除紧跟在分类标题后的第一个高危点
        .replace(/(?!<\/strong>)<br>(&#8226;\s*高危点:)/g, '<br><br>$1')
        // 添加空行在解析完成后，确保与下一个高危点之间有足够间隔
        .replace(/(解析:.*?)<br>(&#8226;\s*高危点:)/g, '$1<br><br>$2')
        // 修改：为每个analysis-item项添加段后空行，但最后一个item除外
        .replace(/^(•\s*[^\n]+)/gm, '<div class="analysis-item">$1</div>')
        // 在每个analysis-item后添加空行，以便区分不同项目
        .replace(/<\/div><div class="analysis-item">/g, '</div><br><div class="analysis-item">')
    })
    
    // 监听检测模式的变化
    const handleModeChange = (val) => {
      if (val) {
        ElMessage.info('已进入检测模式，正在加载术语...')
        // 进入检测模式时直接开始随机测试，添加短暂延迟确保DOM已更新
        setTimeout(() => {
          startNewTest()
        }, 100)
      } else {
        exitTestMode()
      }
    }
    
    // 切换到检测模式
    const startNewTest = () => {
      if (allTerms.value.length === 0) {
        ElMessage.warning('没有可用的术语进行测试')
        return
      }
      
      // 随机选择一个术语
      const randomIndex = Math.floor(Math.random() * allTerms.value.length)
      selectedTerm.value = allTerms.value[randomIndex]
      userInput.value = ''
      feedbackMessage.value = ''
      feedbackType.value = ''
    }
    
    // 结束当前测试
    const exitCurrentTest = () => {
      selectedTerm.value = null
      userInput.value = ''
      feedbackMessage.value = ''
    }
    
    // 退出检测模式
    const exitTestMode = () => {
      testMode.value = false
      exitCurrentTest()
    }
    
    // 检查答案
    const checkAnswer = () => {
      if (!selectedTerm.value || !userInput.value.trim()) {
        feedbackMessage.value = '请输入翻译'
        feedbackType.value = 'error'
        return
      }
      
      // 获取标准答案和用户输入，转为小写进行比较
      const standardAnswer = selectedTerm.value.translation.toLowerCase()
      const userAnswer = userInput.value.trim().toLowerCase()
      
      if (userAnswer === standardAnswer) {
        feedbackMessage.value = '正确！'
        feedbackType.value = 'success'
        
        // 答案正确时，短暂延迟后自动跳转到下一个术语
        setTimeout(() => {
          nextTerm()
        }, 1000) // 1秒后自动跳转，让用户有时间看到反馈
      } else {
        feedbackMessage.value = '不正确，请重试或查看答案'
        feedbackType.value = 'error'
      }
    }
    
    // 显示答案
    const showAnswer = () => {
      if (!selectedTerm.value) return
      
      feedbackMessage.value = `正确答案：${selectedTerm.value.translation}`
      feedbackType.value = 'info'
    }
    
    // 下一个术语
    const nextTerm = () => {
      startNewTest()
    }
    
    // 在组件挂载后，添加自定义拖拽和调整大小的功能增强
    onMounted(() => {
      setTimeout(() => {
        // 获取dialog元素
        const dialogEl = document.querySelector('.ai-result-dialog .el-dialog');
        if (dialogEl) {
          // 如果Element Plus的内置draggable不够用，这里提供更灵活的拖拽实现
          makeDialogMoreDraggable(dialogEl);
        }
      }, 100); // 短暂延迟确保DOM已完全渲染
    });

    // 增强拖拽功能
    const makeDialogMoreDraggable = (element) => {
      // 确保拖拽时dialog在视口内
      const onDrag = () => {
        const rect = element.getBoundingClientRect();
        if (rect.left < 0) element.style.left = '0px';
        if (rect.top < 0) element.style.top = '0px';
        if (rect.right > window.innerWidth) element.style.left = (window.innerWidth - rect.width) + 'px';
        if (rect.bottom > window.innerHeight) element.style.top = (window.innerHeight - rect.height) + 'px';
      };

      // 监听拖拽事件
      element.addEventListener('dragend', onDrag);
    };

    const handleSave = async () => {
      const currentFile = store.state.currentFile
      if (!currentFile) {
        ElMessage.warning('请先选择文件')
        return
      }

      try {
        if (isAdmin.value) {
          // 管理员保存方式 - 保存到文件
          let filePath = ''
          
          // 获取ContentArea组件实例，检查是否有分段文章
          const contentAreaInstance = store.state.contentAreaInstance;
          const hasMultiplePassages = contentAreaInstance && 
                                    contentAreaInstance.passages && 
                                    contentAreaInstance.passages.length > 1;
          
          // 如果是分段文章且选中了某个段落，需要添加p1或p2标识
          let segmentPrefix = '';
          if (hasMultiplePassages && contentAreaInstance.getCurrentPassages) {
            const passageInfo = contentAreaInstance.getCurrentPassages();
            const currentIndex = passageInfo.currentIndex;
            
            if (currentIndex !== null) {
              segmentPrefix = `.p${currentIndex + 1}`;
            }
          }
          
          // 根据对话框标题决定保存的文件路径，与原始生成文件保持一致
          switch (props.title) {
            case '翻译评分结果':
            case 'P1 翻译评分结果':
            case 'P2 翻译评分结果':
              filePath = currentFile.path.replace(/\.json$/, `${segmentPrefix}.scoring.txt`)
              break
            case '译文':
            case 'P1 译文':
            case 'P2 译文':
              filePath = currentFile.path.replace(/\.txt$/, `${segmentPrefix}.machinetranslation.json`)
              break
            case '术语':
            case 'P1 术语':
            case 'P2 术语':
            case '术语检查结果':
              filePath = currentFile.path.replace(/\.json$/, `${segmentPrefix}.term.txt`)
              break
            case '解析':
            case 'P1 解析':
            case 'P2 解析':
            case '解析结果':
              filePath = currentFile.path.replace(/\.json$/, `${segmentPrefix}.analysis.txt`)
              break
            default:
              filePath = currentFile.path.replace(/\.txt$/, `${segmentPrefix}.result.json`)
          }
          
          console.log(`保存对话框内容到文件：${filePath}`)
          await ApiService.saveTranslation(filePath, editableContent.value.trim())
          ElMessage.success('保存成功')
          dialogVisible.value = false
        } else {
          // 非管理员不需要保存，因为他们只能查看管理员保存的内容
          ElMessage.warning('只有管理员可以保存修改')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error(error.message || '保存失败')
      }
    }

    const handleSaveToSupabase = async () => {
      const currentFile = store.state.currentFile
      if (!currentFile) {
        ElMessage.warning('请先选择文件')
        return
      }

      try {
        // 使用本地存储保存评分结果
        const scoreData = {
          id: Date.now().toString(),
          user_id: 'local_user',
          file_path: currentFile.path,
          content: props.content,
          created_at: new Date().toISOString()
        }

        // 从本地存储获取现有记录
        const existingScores = JSON.parse(localStorage.getItem('scores') || '[]')
        const existingIndex = existingScores.findIndex(score => 
          score.user_id === 'local_user' && score.file_path === currentFile.path
        )

        if (existingIndex !== -1) {
          // 更新现有记录
          existingScores[existingIndex] = {
            ...existingScores[existingIndex],
              content: props.content,
              updated_at: new Date().toISOString() 
          }
          ElMessage.success('评分结果已更新')
        } else {
          // 创建新记录
          existingScores.push(scoreData)
          ElMessage.success('评分结果已保存')
        }

        // 保存到本地存储
        localStorage.setItem('scores', JSON.stringify(existingScores))
      } catch (error) {
        console.error('保存到本地存储失败:', error)
        ElMessage.error('保存失败: ' + error.message)
      }
    }

    const handleClear = async () => {
      try {
        const currentFile = store.state.currentFile
        if (!currentFile) {
          ElMessage.warning('请先选择文件')
          return
        }

        if (isAdmin.value) {
          // 管理员清空方式 - 清空文件内容
          let filePath = ''
          
          // 获取ContentArea组件实例，检查是否有分段文章
          const contentAreaInstance = store.state.contentAreaInstance;
          const hasMultiplePassages = contentAreaInstance && 
                                    contentAreaInstance.passages && 
                                    contentAreaInstance.passages.length > 1;
          
          // 如果是分段文章且选中了某个段落，需要添加p1或p2标识
          let segmentPrefix = '';
          if (hasMultiplePassages && contentAreaInstance.getCurrentPassages) {
            const passageInfo = contentAreaInstance.getCurrentPassages();
            const currentIndex = passageInfo.currentIndex;
            
            if (currentIndex !== null) {
              segmentPrefix = `.p${currentIndex + 1}`;
            }
          }
          
          // 根据对话框标题决定清空的文件路径，与原始生成文件保持一致
          switch (props.title) {
            case '翻译评分结果':
            case 'P1 翻译评分结果':
            case 'P2 翻译评分结果':
              filePath = currentFile.path.replace(/\.json$/, `${segmentPrefix}.scoring.txt`)
              break
            case '译文':
            case 'P1 译文':
            case 'P2 译文':
              filePath = currentFile.path.replace(/\.txt$/, `${segmentPrefix}.machinetranslation.json`)
              break
            case '术语':
            case 'P1 术语':
            case 'P2 术语':
            case '术语检查结果':
              filePath = currentFile.path.replace(/\.json$/, `${segmentPrefix}.term.txt`)
              break
            case '解析':
            case 'P1 解析':
            case 'P2 解析':
            case '解析结果':
              filePath = currentFile.path.replace(/\.json$/, `${segmentPrefix}.analysis.txt`)
              break
            default:
              filePath = currentFile.path.replace(/\.txt$/, `${segmentPrefix}.result.json`)
          }
          
          console.log(`清空文件内容：${filePath}`)
          await ApiService.saveTranslation(filePath, '')
          editableContent.value = ''
          ElMessage.success('已清空内容')
          dialogVisible.value = false
        } else {
          // 非管理员不能清空内容
          ElMessage.warning('只有管理员可以清空内容')
        }
      } catch (error) {
        console.error('清空失败:', error)
        ElMessage.error(error.message || '清空失败')
      }
    }

    const decreaseFontSize = () => {
      if (fontSize.value > 50) {
        fontSize.value -= 10
      }
    }

    const increaseFontSize = () => {
      if (fontSize.value < 200) {
        fontSize.value += 10
      }
    }

    const resetFontSize = () => {
      fontSize.value = 100
    }

    const decreaseLineHeight = () => {
      if (lineHeight.value > 1.5) {
        lineHeight.value = parseFloat((lineHeight.value - 0.1).toFixed(1))
      }
    }

    const increaseLineHeight = () => {
      if (lineHeight.value < 3) {
        lineHeight.value = parseFloat((lineHeight.value + 0.1).toFixed(1))
      }
    }
    
    // 确保lineHeight格式化为一位小数
    watch(lineHeight, (val) => {
      lineHeight.value = parseFloat(val.toFixed(1))
    })

    const getTitleClass = (title) => {
      if (title === '译文' || title.includes('译文')) {
        return 'title-translation'
      } else if (title === '术语' || title === '术语检查结果' || title.includes('术语')) {
        return 'title-term'
      } else if (title === '解析' || title === '解析结果' || title.includes('解析')) {
        return 'title-analysis'
      } else if (title === '会员中心') {
        return 'title-member'
      }
      return ''
    }

    return {
      dialogVisible,
      formattedContent,
      editableContent,
      handleSave,
      handleSaveToSupabase,
      handleClear,
      isAdmin,
      isLoggedIn,
      testMode,
      userInput,
      selectedTerm,
      feedbackMessage,
      feedbackType,
      handleModeChange,
      startNewTest,
      exitTestMode,
      checkAnswer,
      showAnswer,
      nextTerm,
      decreaseFontSize,
      increaseFontSize,
      resetFontSize,
      fontSize,
      lineHeight,
      decreaseLineHeight,
      increaseLineHeight,
      getTitleClass,
    }
  }
}
</script>

<style scoped>
.ai-result-dialog {
  font-family: "Microsoft YaHei", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.result-content {
  max-height: 70vh;
  overflow-y: auto;
  padding: 0 5px;
  line-height: 2.2;
  font-size: 15px;
  white-space: pre-wrap;
  text-align: left;
  user-select: text;
  cursor: text;
  margin: 0;
}

.content-header {
  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #eee;
}

.mode-description {
  margin-left: 10px;
  font-size: 13px;
  color: #909399;
}

.test-mode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  width: 100%;
}

.test-panel {
  margin-top: 20px;
  padding: 25px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
  width: 80%;
  max-width: 600px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.selected-term {
  margin-bottom: 20px;
  font-size: 18px;
  text-align: center;
}

.selected-term strong {
  color: #409EFF;
  font-size: 22px;
  display: block;
  margin-top: 8px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;
  margin-bottom: 15px;
  justify-content: center;
}

.feedback {
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  font-weight: bold;
  text-align: center;
}

.feedback.success {
  background-color: #f0f9eb;
  color: #67c23a;
}

.feedback.error {
  background-color: #fef0f0;
  color: #f56c6c;
}

.feedback.info {
  background-color: #f4f4f5;
  color: #909399;
}

.result-content :deep(.el-textarea__inner) {
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: 10px;
  min-height: 300px;
}

.result-content :deep(strong) {
  color: #409EFF;
  font-weight: 600;
}

.result-content :deep(.category) {
  color: #67C23A;
  margin-top: 20px;
  margin-bottom: 12px;
  display: block;
}

.result-content :deep(.translation) {
  color: #909399;
  margin-left: 4px;
}

.result-content :deep(.score) {
  color: #F56C6C;
  font-weight: bold;
  font-size: 18px;
  display: block;
  margin-bottom: 10px;
}

.result-content :deep(.deduction) {
  color: #E6A23C;
}

/* 自定义滚动条样式 */
.result-content::-webkit-scrollbar {
  width: 8px;
}

.result-content::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.result-content::-webkit-scrollbar-track {
  background: #f5f5f5;
}

:deep(.el-dialog__body) {
  padding: 0;
  margin-top: 0;
  padding-bottom: 0;
}

:deep(.el-dialog__header) {
  padding: 0px;
  margin-bottom: 0;
  border-bottom: 1px solid #e8e8e8;
  cursor: move; /* 指示可拖拽 */
  height: 22px;
  line-height: 24px;
  box-sizing: border-box;
}

:deep(.el-dialog__headerbtn) {
  top: 4px;
  right: 5px;
}

.dialog-footer {
  padding: 0px;
  text-align: right;
  border-top: 0;
  height: 26px;
  line-height: 26px;
  box-sizing: border-box;
}

:deep(.el-dialog__footer) {
  padding: 0;
  line-height: 0;
  margin-top: 0;
}

:deep(.el-button) {
  padding: 0 8px;
  line-height: 22px;
  height: 22px;
  font-size: 12px;
  margin: 0 5px 0 0;
  position: relative;
  bottom: -1px;
}

:deep(.el-button:last-child) {
  margin-right: 5px;
}

:deep(.el-dialog) {
  overflow: visible !important;
  position: absolute !important;
}

:deep(.el-switch) {
  --el-switch-on-color: #409EFF;
}

/* 确保对话框内部没有多余的边距 */
:deep(.el-dialog__body), :deep(.el-dialog__content) {
  overflow: visible;
  box-sizing: border-box;
  margin: 0;
}

/* 确保第一个类别前没有额外空白 */
:deep(.result-content > div > .category:first-of-type) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* 针对可能的br标签导致的空白 */
:deep(.result-content > div > br:first-child) {
  display: none;
}

/* 消除内容区域第一个元素的顶部边距 */
.result-content :deep(.category):first-child {
  margin-top: 0;
}

.result-content > div:first-child {
  margin-top: 0;
}

/* 确保查看模式的内容没有顶部空行 */
.result-content > div[v-html] {
  margin-top: 0;
}

/* 更强的消除顶部空白规则 */
.formatted-content {
  margin-top: 0 !important;
  padding-top: 8px !important;
  margin-bottom: 0 !important;
  padding-bottom: 8px !important;
}

.formatted-content :deep(*:first-child) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* 高危点样式 */
.result-content :deep(.high-risk-point) {
  color: #f56c6c;
  font-weight: bold;
}

/* 机器翻译结果样式 */
.machine-translation-result {
  display: block;
  margin-top: 0;
}

.machine-translation-result p {
  text-indent: 2em; /* 添加首行缩进，包括第一段 */
  margin-bottom: 1.5em; /* 段落之间的间距 */
  text-align: justify; /* 两端对齐 */
  line-height: inherit; /* 使用继承的行高 */
}

/* 强制为机器翻译结果添加段落样式 */
.result-content :deep(.machine-translation-content) {
  text-align: justify;
  text-justify: inter-word;
  line-height: inherit; /* 使用继承的行高 */
  padding: 0;
}

.result-content :deep(.translation-paragraph) {
  display: block;
  margin: 0 0 20px 0 !important; /* 减少段落底部间距 */
  text-indent: 2em !important; /* 确保首行缩进 */
  hyphens: auto;
  position: relative;
  line-height: inherit; /* 使用继承的行高 */
}

.result-content :deep(.translation-paragraph:last-child) {
  margin-bottom: 0 !important; /* 最后一个段落没有底部间距 */
}

/* 为非管理员用户的机器翻译结果添加额外处理 */
.result-content:has(> .machine-translation-result) {
  overflow-y: auto;
  white-space: normal !important;
}

:deep(.el-dialog__title) {
  font-size: 14px;
  line-height: 1;
  font-weight: bold;
  padding-left: 8px;
  display: inline-block;
}

/* 优化术语检查结果的显示格式 */
:deep(.analysis-item) {
  margin: 6px 0;
  line-height: 1.8;
}

/* 确保对话框正确显示 */
.el-dialog__wrapper {
  z-index: 2000;
}

/* 新增评分结果样式 */
.result-content :deep(.error-item) {
  color: #F56C6C;
  font-weight: 600;
  font-size: 15px;
  margin-top: 12px;
  margin-bottom: 4px;
  padding-top: 6px;
}

.result-content :deep(.error-item) + :deep(.detail-item) {
  margin-top: 4px !important;
}

.result-content :deep(.error-detail) {
  margin: 4px 0 !important;
  line-height: 1.7 !important;
}

.result-content :deep(.error-detail strong) {
  color: #303133;
  margin-right: 5px;
}

/* 为评分结果对话框增加更合适的高度和滚动条 */
.ai-result-dialog .result-content {
  max-height: 75vh; /* 增加最大高度 */
  overflow-y: auto;
  padding-right: 15px; /* 增加右侧padding，避免滚动条遮挡内容 */
  line-height: 1.5; /* 默认行高 */
}

/* 优化评分结果内容的行距 */
.result-content :deep(.error-detail) {
  margin: 4px 0 !important;
  line-height: 1.7 !important;
}

/* 调整总分与详情之间的间距 */
.result-content :deep(h3) + :deep(h4) {
  margin-top: 8px !important;
}

/* 优化类别标题间距 */
.result-content :deep(h4) {
  margin-top: 8px !important;
  margin-bottom: 4px !important;
}

/* 调整错误项目之间的间距 */
.result-content :deep(.error-item) {
  margin-top: 12px !important;
  margin-bottom: 4px !important;
}

/* 使对话框可调整大小 */
:deep(.el-dialog) {
  resize: both !important; /* 允许用户手动调整对话框大小 */
  overflow: hidden !important;
  min-width: 400px;
  min-height: 300px;
}

/* 新增翻译评分结果样式 - 参考解析对话框样式 */
.result-content :deep(.score-header) {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.result-content :deep(.score-title) {
  color: #67C23A !important;
  font-weight: 600;
  margin-right: 5px;
  font-size: 16px;
}

.result-content :deep(.score-value) {
  color: #F56C6C;
  font-weight: bold;
  font-size: 16px;
}

.result-content :deep(.no-issues-text) {
  font-size: 15px;
  margin: 10px 0;
  line-height: 1.6;
}

/* 添加字体大小控制样式 */
.font-size-control {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: 8px 0;
  padding: 4px 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.font-size-value {
  margin: 0 8px;
  min-width: 40px;
  text-align: center;
  font-size: 14px;
}

/* 确保测试面板也应用字体大小和行距设置 */
.test-mode-container {
  font-size: v-bind('fontSize + "%"');
  line-height: v-bind('lineHeight');
}

/* 移除旧的字体大小控制样式 */
.result-content .font-size-control {
  display: none;
}

/* 添加自定义标题栏样式 */
.custom-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 22px;
  padding-left: 8px;
  box-sizing: border-box;
}

/* 字体大小和行距控制按钮样式 */
.custom-header .font-size-control {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: 0;
  padding: 0;
  background-color: transparent;
  border-radius: 0;
  position: relative;
  right: 30px; /* 为关闭按钮留出空间 */
}

.custom-header .font-size-value,
.custom-header .line-height-value {
  margin: 0 4px;
  min-width: 25px;
  text-align: center;
  font-size: 12px;
  color: #606266;
}

.line-height-label {
  font-size: 12px;
  color: #606266;
  margin: 0 4px;
}

.control-divider {
  width: 1px;
  height: 14px;
  background-color: #dcdfe6;
  margin: 0 6px;
}

.custom-header .el-button {
  padding: 3px;
  margin: 0 2px;
}

/* 对话框标题样式 */
.title-translation {
  color: #409EFF !important;
  font-weight: bold;
  font-size: 18px;
  background: linear-gradient(to right, #409EFF, #8cc5ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.title-term {
  color: #67C23A !important;
  font-weight: bold;
  font-size: 18px;
  background: linear-gradient(to right, #67C23A, #95d475);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.title-analysis {
  color: #E6A23C !important;
  font-weight: bold;
  font-size: 18px;
  background: linear-gradient(to right, #E6A23C, #f3d19e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.title-member {
  color: #9365FB !important;
  font-weight: bold;
  font-size: 18px;
  background: linear-gradient(to right, #9365FB, #B995FD);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.result-content :deep(.score-value) {
  color: #F56C6C;
  font-weight: bold;
  font-size: 16px;
}

.result-content :deep(.no-issues-text) {
  font-size: 15px;
  margin: 10px 0;
  line-height: 1.6;
}

.result-content :deep(.section-title) {
  color: #67C23A !important;
  font-weight: 600;
  font-size: 16px;
  margin-top: 15px;
  margin-bottom: 5px;
}

.result-content :deep(.category-title) {
  color: #67C23A !important;
  font-weight: 600;
  font-size: 16px;
  margin-top: 10px;
  margin-bottom: 5px;
}

.result-content :deep(.error-item) {
  color: #F56C6C;
  font-weight: 600;
  font-size: 15px;
  margin-top: 12px;
  margin-bottom: 4px;
  padding-top: 6px;
}

.result-content :deep(.error-deduction) {
  color: #E6A23C;
  margin-left: 5px;
  font-weight: normal;
}

.result-content :deep(.detail-item) {
  margin: 4px 0 !important;
  line-height: 1.8 !important;
  font-size: 14px;
  padding: 2px 0 !important;
}

/* 为不同类型的详情项添加特殊样式 */
.result-content :deep(.detail-original) strong {
  color: #409EFF;
}

.result-content :deep(.detail-translation) strong {
  color: #409EFF;
}

.result-content :deep(.detail-suggestion) strong {
  color: #409EFF;
}

.result-content :deep(.detail-reason) strong {
  color: #409EFF;
}

/* 确保理由前没有空行，但理由后有空行 */
.result-content :deep(.detail-reason) {
  margin-bottom: 10px;
}

/* 将错误项与第一个详情项（原文）紧密连接 */
.result-content :deep(.error-item) + :deep(.detail-original) {
  margin-top: 4px !important;
}

.result-content :deep(.error-separator) {
  height: 12px;
}

.result-content :deep(.category-separator) {
  height: 16px;
}

/* 评分结果和解析结果的黄色边框样式 */
.formatted-content.score-result {
  border: 1px solid #E6A23C;
  border-radius: 4px;
  padding: 12px 15px;
  background-color: #FEFCF7;
}

.result-content :deep(.detail-group) {
  border-left: 2px solid #f0f0f0;
  padding-left: 8px;
  margin-bottom: 8px;
  margin-top: 4px;
  background-color: #fafafa;
  border-radius: 0 4px 4px 0;
}
</style>